// Boards: MyDecks (answer tab), MyDecksQuestionsEmpty (question tab).
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { t, tx } from '../../i18n';
import { Bdi } from '../../i18n/direction';
import { cardCount, group } from './decks';
import { BackIcon, GeneralError, LevelTag, PlusIcon, onTap } from './parts';
import LanguageMenu from './LanguageMenu';

const LockIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true"><rect x="5" y="11" width="14" height="9" rx="2"></rect><path d="M8 11V8a4 4 0 0 1 8 0v3"></path></svg>
);

export default function MyDecks({ tab, decks, loadFailed, onLoad, onTab, onBack, onOpenDeck, onAuthFailure }) {
  const [createFailed, setCreateFailed] = useState(false);
  const creating = useRef(false);

  useEffect(() => {
    onLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (decks === null && !loadFailed) return null; // loading

  const swatch = tab === 'question' ? 'swatch swatch-q' : 'swatch swatch-a';
  const ofTab = (decks ?? []).filter(d => d.type === tab);
  const own = ofTab.filter(d => group(d) === 'own');
  const shared = ofTab.filter(d => group(d) === 'shared');
  const starters = ofTab.filter(d => d.starter);

  const create = async () => {
    if (creating.current) return;
    creating.current = true;
    setCreateFailed(false);
    try {
      const res = await axios.post('decks', { type: tab, name: t('deck.defaultName') });
      onOpenDeck(res.data.id);
    } catch (e) {
      if (!(await onAuthFailure(e))) setCreateFailed(true);
    } finally {
      creating.current = false;
    }
  };

  const tabButton = (type, label) => (
    <button type="button" className={tab === type ? 'seg-btn seg-on' : 'seg-btn'} role="tab"
      aria-selected={tab === type ? 'true' : 'false'} onClick={() => tab !== type && onTab(type)}>{label}</button>
  );

  return (
    <main className="screen">
      <header className="topbar">
        <a className="icon-btn" href="#back" onClick={onTap(onBack)} aria-label={t('common.back')}><BackIcon /></a>
        <h1 className="topbar-title">{t('home.myDecks')}</h1>
        <LanguageMenu />
      </header>
      <div className="body" style={{ gap: 12, overflowY: 'auto' }}>
        <div className="seg" role="tablist" aria-label={t('decks.deckType')}>
          {tabButton('answer', t('home.answerCards'))}
          {tabButton('question', t('home.questionCards'))}
        </div>
        <a className="btn btn-dark btn-block" href="#new" onClick={onTap(create)}>
          <PlusIcon size="20" />{tab === 'question' ? t('decks.newQuestion') : t('decks.newAnswer')}
        </a>
        {(loadFailed || createFailed) && <GeneralError />}

        <h2 className="sec" style={{ marginTop: 4 }}>{t('decks.mine')}</h2>
        {own.length === 0
          ? <p className="hint">{t('decks.noneYet')}</p>
          : (
            <ul className="list">
              {own.map(d => (
                <li key={d.id}>
                  <a className="item" href="#deck" onClick={onTap(() => onOpenDeck(d.id))}>
                    <span className={swatch} aria-hidden="true"></span>
                    <span className="item-main"><span className="item-title"><Bdi>{d.name}</Bdi></span><span className="item-sub">{d.share_count > 0 ? t('decks.ownShared', { cards: cardCount(d.card_count), count: d.share_count }) : cardCount(d.card_count)}</span></span>
                  </a>
                </li>
              ))}
            </ul>
          )}

        <h2 className="sec" style={{ marginTop: 4 }}>{t('decks.sharedWithMe')}</h2>
        {shared.length === 0
          ? <p className="hint">{t('decks.sharedEmpty')}</p>
          : (
            <ul className="list">
              {shared.map(d => (
                <li key={d.id}>
                  <a className="item" href="#deck" onClick={onTap(() => onOpenDeck(d.id))}>
                    <span className={swatch} aria-hidden="true"></span>
                    <span className="item-main"><span className="item-title"><Bdi>{d.name}</Bdi></span><span className="item-sub">{tx('decks.sharedRow', { owner: d.owner, cards: cardCount(d.card_count) })}</span></span>
                    <LevelTag level={d.access} />
                  </a>
                </li>
              ))}
            </ul>
          )}

        <h2 className="sec" style={{ marginTop: 4 }}>{t('decks.starter')}</h2>
        <ul className="list">
          {starters.map(d => (
            <li key={d.id} className="item">
              <span className={swatch} aria-hidden="true"></span>
              <span className="item-main"><span className="item-title"><Bdi>{d.name}</Bdi></span><span className="item-sub">{cardCount(d.card_count)}</span></span>
              <span className="tag tag-lock"><LockIcon />{t('decks.locked')}</span>
            </li>
          ))}
        </ul>
      </div>
    </main>
  );
}
